import React, { useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Card,
  CardActionArea,
  useTheme,
  useMediaQuery,
  Chip
} from '@mui/material';
import { motion } from 'framer-motion';
import { ArrowForward } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import windowsImg from '../../../../assets/images/category_windows.jpg';
import officeImg from '../../../../assets/images/category_msoffice.jpg';
import serverImg from '../../../../assets/images/category_server.jpg';
import adobeImg from '../../../../assets/images/category_adobe.jpg';
import othersImg from '../../../../assets/images/category_others.jpg';
import comboImg from '../../../../assets/images/category_combo.jpg';

const categories = [
  {
    title: 'Windows OS',
    subtitle: 'Win 10, Win 11 & more',
    image: windowsImg,
    path: '/shop?category=windows',
    categoryParam: 'windows',
    accent: '#0e6ada',
    badge: 'Most Popular',
    badgeColor: 'primary',
    gridMd: 4,
  },
  {
    title: 'MS Office',
    subtitle: 'Office 2021, 2024 & more',
    image: officeImg,
    path: '/shop?category=ms-office',
    categoryParam: 'ms-office',
    accent: '#e65100',
    badge: 'Best Seller',
    badgeColor: 'warning',
    gridMd: 4,
  },
  {
    title: 'Windows Server',
    subtitle: 'Server 2019, 2022, 2025',
    image: serverImg,
    path: '/shop?category=servers',
    categoryParam: 'servers',
    accent: '#00838f',
    badge: 'Enterprise',
    badgeColor: 'info',
    gridMd: 4,
  },
  {
    title: 'Adobe',
    subtitle: 'Photoshop, Illustrator & more',
    image: adobeImg,
    path: '/shop?category=adobe',
    categoryParam: 'adobe',
    accent: '#c62828',
    badge: 'Creative',
    badgeColor: 'error',
    gridMd: 4,
  },
  {
    title: 'Others',
    subtitle: 'AutoDesk, CorelDraw, Tally',
    image: othersImg,
    path: '/shop?category=others',
    categoryParam: 'others',
    accent: '#6a1b9a',
    badge: 'New Arrivals',
    badgeColor: 'secondary',
    gridMd: 4,
  },
  {
    title: 'Combo Deals',
    subtitle: 'Bundle & save more',
    image: comboImg,
    path: '/shop?category=combo',
    categoryParam: 'combo',
    accent: '#2e7d32',
    badge: 'Save More',
    badgeColor: 'success',
    gridMd: 4,
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: 'easeOut' }
  })
};

const TopCategories = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [hoveredIdx, setHoveredIdx] = useState(null);

  return (
    <Box
      sx={{
        py: isMobile ? 5 : 8,
        background: 'linear-gradient(160deg, #f0f4ff 0%, #ffffff 50%, #f5f0ff 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background decoration */}
      <Box
        sx={{
          position: 'absolute',
          top: -80,
          right: -80,
          width: 320,
          height: 320,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(14,106,218,0.07) 0%, transparent 70%)',
          pointerEvents: 'none'
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: -60,
          left: -60,
          width: 240,
          height: 240,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(106,27,154,0.06) 0%, transparent 70%)',
          pointerEvents: 'none'
        }}
      />

      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: isMobile ? 4 : 6 }}>
          <Typography
            variant="overline"
            sx={{
              color: 'primary.main',
              fontWeight: 700,
              letterSpacing: 3,
              fontSize: '0.75rem',
              display: 'block',
              mb: 1
            }}
          >
            Browse by Category
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 900,
              fontSize: isMobile ? '2rem' : '3rem',
              background: 'linear-gradient(135deg, #1a237e 0%, #0e6ada 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1.5,
            }}
          >
            Our Top Categories
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: 500, mx: 'auto', lineHeight: 1.7 }}
          >
            Genuine software licenses with instant delivery &amp; lifetime activation. Click any category to explore.
          </Typography>
          <Box sx={{ width: 70, height: 4, background: 'linear-gradient(90deg, #1565c0, #0e6ada)', borderRadius: 2, mx: 'auto', mt: 2 }} />
        </Box>

        {/* Category Cards Grid */}
        <Grid container spacing={isMobile ? 2.5 : 3}>
          {categories.map((cat, index) => (
            <Grid item xs={12} sm={6} md={cat.gridMd} key={cat.title}>
              <motion.div
                custom={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-50px' }}
                variants={cardVariants}
                whileHover={{ y: -6 }}
                transition={{ type: 'spring', stiffness: 300 }}
                onMouseEnter={() => setHoveredIdx(index)}
                onMouseLeave={() => setHoveredIdx(null)}
              >
                <Card
                  onClick={() => navigate(cat.path)}
                  sx={{
                    borderRadius: 3,
                    position: 'relative',
                    height: isMobile ? 220 : isTablet ? 240 : 260,
                    overflow: 'hidden',
                    cursor: 'pointer',
                    boxShadow: hoveredIdx === index
                      ? `0 20px 50px rgba(0,0,0,0.22), 0 0 0 3px ${cat.accent}55`
                      : '0 4px 20px rgba(0,0,0,0.10)',
                    transition: 'box-shadow 0.3s ease',
                  }}
                >
                  <CardActionArea sx={{ height: '100%' }}>
                    {/* Background Image */}
                    <Box
                      component="img"
                      src={cat.image}
                      alt={cat.title}
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transform: hoveredIdx === index ? 'scale(1.07)' : 'scale(1)',
                        transition: 'transform 0.5s ease',
                      }}
                    />

                    {/* Gradient Overlay */}
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        background: hoveredIdx === index
                          ? `linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.75) 100%)`
                          : `linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.65) 100%)`,
                        transition: 'background 0.3s ease',
                      }}
                    />

                    {/* Badge */}
                    <Box sx={{ position: 'absolute', top: 14, right: 14 }}>
                      <Chip
                        label={cat.badge}
                        size="small"
                        color={cat.badgeColor}
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.68rem',
                          height: 22,
                          backdropFilter: 'blur(4px)',
                        }}
                      />
                    </Box>

                    {/* Content */}
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        p: isMobile ? 2 : 2.5,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                      }}
                    >
                      <Typography
                        variant={isMobile ? 'h6' : 'h5'}
                        sx={{
                          fontWeight: 800,
                          color: '#fff',
                          lineHeight: 1.2,
                          textShadow: '0 2px 8px rgba(0,0,0,0.5)',
                        }}
                      >
                        {cat.title}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'rgba(255,255,255,0.82)',
                          fontSize: '0.75rem',
                          mb: 1,
                          textShadow: '0 1px 4px rgba(0,0,0,0.4)',
                        }}
                      >
                        {cat.subtitle}
                      </Typography>

                      {/* View All Button */}
                      <Button
                        variant="contained"
                        size="small"
                        endIcon={<ArrowForward sx={{ fontSize: '0.9rem' }} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(cat.path);
                        }}
                        sx={{
                          alignSelf: 'flex-start',
                          bgcolor: cat.accent,
                          color: '#fff',
                          fontWeight: 700,
                          fontSize: '0.78rem',
                          py: 0.6,
                          px: 1.8,
                          borderRadius: 5,
                          textTransform: 'none',
                          opacity: hoveredIdx === index ? 1 : 0.85,
                          transform: hoveredIdx === index ? 'translateY(0)' : 'translateY(4px)',
                          transition: 'all 0.25s ease',
                          boxShadow: `0 4px 14px ${cat.accent}66`,
                          '&:hover': {
                            bgcolor: cat.accent,
                            boxShadow: `0 6px 20px ${cat.accent}99`,
                          }
                        }}
                      >
                        View All
                      </Button>
                    </Box>
                  </CardActionArea>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default TopCategories;
